/**
 * Life4Earth V2 — hook for a community's designated guidelines + the
 * current user's acknowledgment state (onboarding chunk A, §7.9).
 *
 * Pattern follows useApiOnboarding.ts (queryCache fetchForKey + cache
 * update event). Pass null to skip fetching (e.g. no org community).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchCommunityGuidelines,
  acknowledgeCommunityGuidelines,
} from "./communityGuidelines";
import type { CommunityGuidelinesResponse } from "./communityGuidelines";
import { fetchForKey, invalidateAndRefetch, CACHE_UPDATE_EVENT } from "./queryCache";

export interface UseApiCommunityGuidelinesResult {
  guidelines: CommunityGuidelinesResponse | null;
  /** Acknowledge the current version, then refresh. */
  acknowledge: () => Promise<void>;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useApiCommunityGuidelines(
  communityGuid: string | null | undefined,
): UseApiCommunityGuidelinesResult {
  const mountedRef = useRef(true);
  const [data, setData] = useState<CommunityGuidelinesResponse | null>(null);
  const [loading, setLoading] = useState(!!communityGuid);
  const [error, setError] = useState<string | null>(null);

  const cacheKey = communityGuid ? `communityGuidelines:${communityGuid}` : null;

  const refetch = useCallback(async () => {
    if (!mountedRef.current || !communityGuid) return;
    try {
      const res = await fetchForKey(
        cacheKey!,
        async () => fetchCommunityGuidelines(communityGuid),
        { force: true },
      );
      if (!mountedRef.current) return;
      setData(res);
      setLoading(false);
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      setLoading(false);
      setError(err instanceof Error ? err.message : "Failed to load guidelines");
    }
  }, [communityGuid, cacheKey]);

  useEffect(() => {
    mountedRef.current = true;
    if (communityGuid) {
      refetch();
    } else {
      setData(null);
      setLoading(false);
      setError(null);
    }
    return () => {
      mountedRef.current = false;
    };
  }, [communityGuid, refetch]);

  useEffect(() => {
    if (!cacheKey) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key === cacheKey) refetch();
    };
    window.addEventListener(CACHE_UPDATE_EVENT, handler);
    return () => window.removeEventListener(CACHE_UPDATE_EVENT, handler);
  }, [cacheKey, refetch]);

  const acknowledge = useCallback(async () => {
    if (!communityGuid) return;
    const res = await acknowledgeCommunityGuidelines(communityGuid);
    if (mountedRef.current) setData(res);
    if (cacheKey) invalidateAndRefetch(cacheKey);
  }, [communityGuid, cacheKey]);

  return { guidelines: data, acknowledge, loading, error, refetch };
}

/**
 * Guidelines + the caller's ack currency for a SET of communities — feeds
 * the general "Read this: {Org} guidelines" Notice (2026-07-08): any active
 * org member with a stale acknowledgment gets the row, at ANY rung (the
 * Explorer checklist variant covers only the invited-org onboarding case).
 * Same per-guid cache keys as the single hook, so acknowledgments and
 * admin edits invalidate both.
 */
export function useApiCommunityGuidelinesMap(
  communityGuids: string[],
): Map<string, CommunityGuidelinesResponse> {
  const mountedRef = useRef(true);
  const [map, setMap] = useState<Map<string, CommunityGuidelinesResponse>>(new Map());

  // Stable join key so the effect reruns only when the SET changes.
  const guidsKey = communityGuids
    .map((g) => String(g).toUpperCase())
    .sort()
    .join(",");

  const refetchAll = useCallback(async () => {
    if (!mountedRef.current || !guidsKey) {
      if (mountedRef.current) setMap(new Map());
      return;
    }
    const guids = guidsKey.split(",").filter(Boolean);
    const results = await Promise.all(
      guids.map(async (g) => {
        try {
          const res = await fetchForKey(
            `communityGuidelines:${g}`,
            async () => fetchCommunityGuidelines(g),
            { force: false },
          );
          return [g, res] as const;
        } catch {
          return [g, null] as const;
        }
      }),
    );
    if (!mountedRef.current) return;
    const next = new Map<string, CommunityGuidelinesResponse>();
    for (const [g, res] of results) {
      if (res) next.set(g, res);
    }
    setMap(next);
  }, [guidsKey]);

  useEffect(() => {
    mountedRef.current = true;
    refetchAll();
    return () => {
      mountedRef.current = false;
    };
  }, [refetchAll]);

  // Any member community's guidelines cache invalidating (ack, admin edit)
  // refreshes the map.
  useEffect(() => {
    if (!guidsKey) return;
    const keys = new Set(guidsKey.split(",").map((g) => `communityGuidelines:${g}`));
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key && keys.has(detail.key)) refetchAll();
    };
    window.addEventListener(CACHE_UPDATE_EVENT, handler);
    return () => window.removeEventListener(CACHE_UPDATE_EVENT, handler);
  }, [guidsKey, refetchAll]);

  return map;
}
